import { useEffect } from 'react';
import { Link } from '@heroui/link';
import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { SortEventData } from '@formkit/drag-and-drop';
import { useTranslation } from 'react-i18next';
import { RxDragHandleDots2 } from 'react-icons/rx';
import { PiMetronomeFill } from 'react-icons/pi';
import { IoMdMusicalNote } from 'react-icons/io';
import { Chip } from '@heroui/chip';

import { Song, Songbook } from '@/core/db.ts';
import { useOrderSongs } from '@/hooks/useSongbook.ts';

interface SongsListTableProps {
  songs: Song[];
  songbook: Songbook;
}

function SongNo({ order }: { order: number }) {
  return (
    <div className="pr-2 opacity-50 w-8 text-center">{order}</div>
  );
}

function SongsListTable({ songs, songbook }: SongsListTableProps) {
  const { t } = useTranslation();
  const { mutateAsync: orderSongs } = useOrderSongs();
  const [ref, items, setValues] = useDragAndDrop<HTMLUListElement, Song>(
    songs,
    {
      selectedClass: 'bg-default-400',
      dragHandle: '.handle',
      // @ts-ignore
      onSort: async (e: SortEventData<Song>) => {
        const newOrder = e.values;

        await orderSongs(newOrder);
      },
    }
  );

  useEffect(() => {
    setValues(songs);
  }, [songs]);

  return (
    <div>
      <ul ref={ref} className="my-8 gap-4 flex flex-col">
        {items.map((song) => (
          <li
            key={song.id}
            className="flex rounded-lg items-stretch hover:bg-default-100 border-1 border-default-400"
          >
            <Link className="p-2 flex grow text-foreground" href={`/songbook/${songbook.id}/song/${song.id}`}>
              <SongNo order={song.order} />
              <div className="ml-2 grow">
                <div>
                  {song.title}
                </div>
                {song.artist && (
                  <div className="text-sm">
                    {song.artist}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                {song.key && (
                  <Chip className="px-2" size="sm" startContent={<IoMdMusicalNote />}>
                    {song.key}
                  </Chip>
                )}
                {song.tempo && (
                  <Chip className="px-2" size="sm" startContent={<PiMetronomeFill />}>
                    {t('songbook.song.bpmValueButton', { value: song.tempo })}
                  </Chip>
                )}
              </div>
            </Link>
            <div className="handle cursor-move flex items-center p-4">
              <RxDragHandleDots2 />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SongsListTable;
